import PropTypes from 'prop-types'
import { memo, useMemo } from 'react'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import Spinner from 'components/elements/Spinner/Spinner'
import TransitionSwap from 'components/elements/TransitionSwap/TransitionSwap'
import classNames from 'lib/util/classNames'
import Connect from './Connect/Connect'
import RetryLogin from './RetryLogin/RetryLogin'
import SignUp from './SignUp/SignUp'
import UserInfo from './UserInfo/UserInfo'
import styles from './UserLogin.module.scss'

/**
 * The `UserLogin` component handles the user login flow.
 *
 * This process can vary and take the form of different steps
 * depending on the user wallet connection & account information
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function UserLogin (props) {
  const { className, isSignUp } = props
  const { appActivityLabel } = useRootContext()
  const { account, isUserActivityReady } = useUserContext()
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )

  /**
   * Memo: The display
   */
  const display = useMemo(() => {
    if (!isUserActivityReady) {
      // Not ready
      return (
        <div className={styles.loadInfo}>
          <Spinner className={styles.spinner} isActive={true} delayShow={0} />
          {appActivityLabel}
        </div>
      )
    }

    if (!account.hasWallet) {
      // User wallet is not connected
      return <Connect isSignUp={isSignUp} />
    }

    if (account.hasAccountLoginError) {
      // There was a problem authenticating the user
      return <RetryLogin />
    }

    if (!account.isSignedIn) {
      // User is not signed up
      return <SignUp />
    }

    // User is signed up
    return <UserInfo />
  }, [account, isUserActivityReady, isSignUp])

  return (
    <div className={classNameOutput}>
      <TransitionSwap animation="accordion" animateFirst={false}>
        {display}
      </TransitionSwap>
    </div>
  )
}

/** @type {object} */
UserLogin.propTypes = {
  className: PropTypes.string,
  isSignUp: PropTypes.bool
}

/** @type {object} */
UserLogin.defaultProps = {
  className: '',
  isSignUp: false
}

// Memoize
export default memo(UserLogin)
